import { useEffect, useMemo, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { authFetch, authFetchRequest } from '../../utils/api';
import { getCookie } from '../../utils/cookies';
import { alertC } from '../dialogService';
import './notifications.css';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'blockquote', 'code-block'],
    ['clean'],
  ],
};

const NotificationsSend = ({ onSent }) => {
  const role = (getCookie('user_role') || '').toLowerCase();
  const isOwner = role === 'owner' || role === 'superadmin';
  const isDealer = role === 'dealer';

  const [dealers, setDealers] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);

  // Form state
  const [recipientType, setRecipientType] = useState(isOwner ? 'dealer' : 'all_sales');
  const [dealerId, setDealerId] = useState('');
  const [includeSales, setIncludeSales] = useState(false);
  const [salesUserId, setSalesUserId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  // Load dealers (owner) or own dealer's salesmen (dealer)
  useEffect(() => {
    if (isOwner) {
      authFetch('/dealers/')
        .then((data) => setDealers((data || []).filter((d) => (d.code || '').toLowerCase() !== 'default')))
        .catch((err) => console.error('Failed to load dealers', err));
    } else if (isDealer) {
      const myDealerId = getCookie('dealer_id');
      if (myDealerId) {
        authFetch(`/dealers/${myDealerId}/users`)
          .then((data) => setSalesUsers((data || []).filter((u) => (u.role_name || '').toLowerCase() === 'sales')))
          .catch((err) => console.error('Failed to load dealer users', err));
      }
    }
  }, [isOwner, isDealer]);

  const recipientPayload = useMemo(() => {
    if (isOwner) {
      if (recipientType === 'dealer') {
        return dealerId ? { type: 'dealer', dealer_id: Number(dealerId), include_sales: includeSales } : null;
      }
      if (recipientType === 'all_dealers')             return { type: 'all_dealers' };
      if (recipientType === 'all_dealers_with_sales')  return { type: 'all_dealers_with_sales' };
    } else if (isDealer) {
      if (recipientType === 'sales') return salesUserId ? { type: 'sales', user_id: Number(salesUserId) } : null;
      if (recipientType === 'all_sales') return { type: 'all_sales' };
    }
    return null;
  }, [isOwner, isDealer, recipientType, dealerId, includeSales, salesUserId]);

  const canSubmit = !sending && subject.trim() && body.trim() && recipientPayload;

  const handleSend = async () => {
    if (!canSubmit) return;
    setSending(true);
    try {
      const res = await authFetchRequest('/notifications/send', {
        subject: subject.trim(),
        body,
        recipients: recipientPayload,
      }, 'POST');
      const count = res?.count ?? 0;
      await alertC(`Sent to ${count} recipient${count === 1 ? '' : 's'}.`);
      // reset form
      setSubject('');
      setBody('');
      setIncludeSales(false);
      if (onSent) onSent();
    } catch (err) {
      console.error('Send failed', err);
      const msg = err?.response?.data?.message || err?.message || 'Send failed';
      await alertC(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="notif-section">
      {isOwner && (
        <div className="notif-field">
          <label>Recipients:</label>
          <select value={recipientType} onChange={(e) => setRecipientType(e.target.value)}>
            <option value="dealer">Specific dealer…</option>
            <option value="all_dealers">All dealers</option>
            <option value="all_dealers_with_sales">All dealers + their salesmen</option>
          </select>
        </div>
      )}

      {isOwner && recipientType === 'dealer' && (
        <>
          <div className="notif-field">
            <label>Dealer:</label>
            <select value={dealerId} onChange={(e) => setDealerId(e.target.value)}>
              <option value="">— select —</option>
              {dealers.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div className="notif-field">
            <label className="notif-checkbox-label">
              <input
                type="checkbox"
                checked={includeSales}
                onChange={(e) => setIncludeSales(e.target.checked)}
              />
              <span>Also send to this dealer's salesmen</span>
            </label>
          </div>
        </>
      )}

      {isDealer && (
        <>
          <div className="notif-field">
            <label>Recipients:</label>
            <select value={recipientType} onChange={(e) => setRecipientType(e.target.value)}>
              <option value="all_sales">All my salesmen</option>
              <option value="sales">Specific salesman…</option>
            </select>
          </div>
          {recipientType === 'sales' && (
            <div className="notif-field">
              <label>Salesman:</label>
              <select value={salesUserId} onChange={(e) => setSalesUserId(e.target.value)}>
                <option value="">— select —</option>
                {salesUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.username}</option>
                ))}
              </select>
            </div>
          )}
        </>
      )}

      <div className="notif-field">
        <label>Subject:</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={255}
          placeholder="Enter subject"
        />
      </div>

      <div className="notif-field">
        <label>Message:</label>
        <ReactQuill theme="snow" value={body} onChange={setBody} modules={QUILL_MODULES} />
      </div>

      <div className="notif-actions">
        <button className="notif-send-btn" disabled={!canSubmit} onClick={handleSend}>
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export default NotificationsSend;
